import type { PropsWithChildren } from "react";
import { ThemeContext } from "../contexts/ThemeContext";
import { useEffect, useMemo, useRef, useState } from "react";

function getPrefersDarKMode() {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

interface PrefersDarkMode {
  value: boolean;
  updatedAt: number;
}

const LS_DARK_MODE_KEY = "prefers-dark-mode" as const;

function getLsDarkMode(): boolean | undefined {
  if (typeof window === "undefined" || !window?.localStorage) {
    return undefined;
  }
  const val = window.localStorage.getItem(LS_DARK_MODE_KEY);
  if (!val) return undefined;
  const prefersDarkMode = JSON.parse(val) as PrefersDarkMode;
  if (prefersDarkMode.updatedAt < Date.now() - 1000 * 60 * 60 * 24) {
    window.localStorage.removeItem(LS_DARK_MODE_KEY);
    return undefined;
  }
  return prefersDarkMode.value;
}

function setLsDarkMode(value: boolean) {
  const now = Date.now();
  window.localStorage.setItem(
    LS_DARK_MODE_KEY,
    JSON.stringify({ value, updatedAt: now }),
  );
}

export interface ThemeProviderProps extends PropsWithChildren {
  organizationSlug: string;
  projectSlug: string;
  defaultOrganizationSlug: string;
  defaultProjectSlug: string;
  as?: "div" | "body";
  forceMode?: "light" | "dark";
}

export const ThemeProvider = ({
  children,
  organizationSlug,
  projectSlug,
  defaultOrganizationSlug,
  defaultProjectSlug,
  forceMode,
  as = "div",
}: ThemeProviderProps) => {
  const container = useRef<HTMLElement>(null);

  const [darkMode, setDarkMode] = useState<boolean>(false);

  const Component = as === "div" ? "div" : "body";

  const actualDarkMode = useMemo(() => {
    if (forceMode) return forceMode === "dark";
    return darkMode;
  }, [darkMode, forceMode]);

  useEffect(() => {
    setDarkMode(getLsDarkMode() ?? getPrefersDarKMode() ?? false);
  }, []);

  function onSetDarkMode(value: boolean) {
    setDarkMode(value);
    setLsDarkMode(value);
  }

  return (
    <ThemeContext.Provider
      value={{
        organizationSlug,
        projectSlug,
        defaultOrganizationSlug,
        defaultProjectSlug,
        darkMode: actualDarkMode,
        setDarkMode: onSetDarkMode,
        container,
      }}
    >
      <Component
        className="themed bg-default-background min-h-screen"
        data-mode={actualDarkMode ? "dark" : "light"}
        ref={container as any}
      >
        {children}
      </Component>
    </ThemeContext.Provider>
  );
};
